"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  type TreeMenuItem,
  useGetIdentity,
  useGo,
  useLink,
  useLogout,
  useMenu,
  useRefineOptions,
} from "@refinedev/core";
import {
  ChevronRight,
  ListIcon,
  LogOut,
  MessagesSquareIcon,
} from "lucide-react";

import { Org } from "@/components/common/org";
import { UserDropdown } from "@/components/common/user-dropdown";
import { ThemeToggle } from "@/components/refine-ui/theme/theme-toggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar as ShadcnSidebar,
  SidebarContent as ShadcnSidebarContent,
  SidebarHeader as ShadcnSidebarHeader,
  SidebarRail as ShadcnSidebarRail,
  SidebarTrigger as ShadcnSidebarTrigger,
  useSidebar as useShadcnSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { findActiveGroup } from "@/lib/sidebar-utils";
import { cn } from "@/lib/utils";

export function Sidebar() {
  const { menuItems, selectedKey } = useMenu();
  const Link = useLink();
  const go = useGo();
  const [focusedGroupKey, setFocusedGroupKey] = useState<string | null>(null);

  const handleClickGroup = (key: string) => {
    const item = menuItems.find((i) => (i.key ?? i.name) === key);
    const defaultPath = item?.meta?.defaultPath as string | undefined;
    if (defaultPath) {
      go({ to: defaultPath });
      return;
    }
    setFocusedGroupKey(key);
  };

  const routeGroup = useMemo(
    () => findActiveGroup(selectedKey, menuItems),
    [selectedKey, menuItems],
  );

  const clickGroup = useMemo(() => {
    if (!focusedGroupKey) return null;
    return menuItems.find((i) => (i.key ?? i.name) === focusedGroupKey) ?? null;
  }, [focusedGroupKey, menuItems]);

  const activeGroup = routeGroup ?? clickGroup;
  const inNestedGroup =
    !!activeGroup && (activeGroup.children?.length ?? 0) > 0;

  // Reconcile click focus with route focus:
  // - When the route enters any group, route focus owns the state — clear click focus.
  // - When the user navigates to a route outside the focused group, clear click focus.
  // The previous-selectedKey ref distinguishes "user clicked a group icon while
  // already on an unrelated route" (don't clear) from "user navigated away" (clear).
  const prevSelectedKeyRef = useRef(selectedKey);
  useEffect(() => {
    const navigated = prevSelectedKeyRef.current !== selectedKey;
    prevSelectedKeyRef.current = selectedKey;

    if (routeGroup && focusedGroupKey) {
      setFocusedGroupKey(null);
      return;
    }
    if (navigated && !routeGroup && focusedGroupKey && selectedKey) {
      const stillInside = menuItems
        .find((i) => (i.key ?? i.name) === focusedGroupKey)
        ?.children?.some((c) => c.key === selectedKey);
      if (!stillInside) setFocusedGroupKey(null);
    }
  }, [routeGroup, selectedKey, focusedGroupKey, menuItems]);

  if (inNestedGroup && activeGroup) {
    return (
      <ShadcnSidebar
        collapsible="none"
        data-mode="rail"
        className="w-[108px] h-svh sticky top-0 self-start"
      >
        <ShadcnSidebarContent
          className={cn("flex flex-row p-0 gap-0 overflow-hidden")}
        >
          <div className="flex flex-col h-full">
            <div className="h-16 w-[54px] shrink-0 flex items-center justify-center bg-sidebar border-r border-b border-sidebar-border">
              <Org compact />
            </div>
            <RailColumn
              topLevelItems={menuItems}
              selectedKey={selectedKey}
              routeGroupKey={routeGroup?.key ?? routeGroup?.name}
              clickGroupKey={focusedGroupKey}
              onClickGroup={handleClickGroup}
              footer={<></>}
            />
          </div>
          <GroupPanel group={activeGroup} selectedKey={selectedKey} />
        </ShadcnSidebarContent>
      </ShadcnSidebar>
    );
  }

  // Flat (today's) layout
  return (
    <FlatSidebar
      menuItems={menuItems}
      selectedKey={selectedKey}
      onClickGroup={handleClickGroup}
    />
  );
}

type FlatSidebarProps = {
  menuItems: TreeMenuItem[];
  selectedKey?: string;
  onClickGroup: (key: string) => void;
};

function FlatSidebar({
  menuItems,
  selectedKey,
  onClickGroup,
}: FlatSidebarProps) {
  const { open } = useShadcnSidebar();
  const Link = useLink();

  return (
    <ShadcnSidebar collapsible="icon">
      <ShadcnSidebarHeader>
        <Org />
      </ShadcnSidebarHeader>
      <ShadcnSidebarContent
        className={cn(
          "transition-discrete",
          "duration-200",
          "flex",
          "flex-col",
          "gap-2",
          "pt-2",
          "pb-2",
          "border-r",
          "border-border",
          {
            "px-3": open,
            "px-1": !open,
          },
        )}
      >
        <FlatSidebarItems
          items={menuItems}
          selectedKey={selectedKey}
          onClickGroup={onClickGroup}
        />
        <div className="flex-1" />
        <ThemeToggle />
        <UserDropdown />
      </ShadcnSidebarContent>
      <ShadcnSidebarRail />
    </ShadcnSidebar>
  );
}

type FlatSidebarItemsProps = {
  items: TreeMenuItem[];
  selectedKey?: string;
  onClickGroup: (key: string) => void;
};

function FlatSidebarItems({
  items,
  selectedKey,
  onClickGroup,
}: FlatSidebarItemsProps) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <SidebarItem
          key={item.key || item.name}
          item={item}
          selectedKey={selectedKey}
          onClickGroup={onClickGroup}
        />
      ))}
    </div>
  );
}

type MenuItemProps = {
  item: TreeMenuItem;
  selectedKey?: string;
  onClickGroup?: (key: string) => void;
};

function SidebarItem({ item, selectedKey, onClickGroup }: MenuItemProps) {
  const { open } = useShadcnSidebar();

  if (item.meta?.group) {
    return (
      <SidebarItemGroup
        item={item}
        selectedKey={selectedKey}
        onClickGroup={onClickGroup}
      />
    );
  }

  if (item.children && item.children.length > 0) {
    if (open) {
      return <SidebarItemCollapsible item={item} selectedKey={selectedKey} />;
    }
    // Was: SidebarItemDropdown. Now: clicking the icon promotes the user
    // into two-rail mode via click-based focus.
    return (
      <SidebarButton
        item={item}
        onClick={() => onClickGroup?.(item.key ?? item.name)}
      />
    );
  }

  return <SidebarItemLink item={item} selectedKey={selectedKey} />;
}

function SidebarItemGroup({ item, selectedKey, onClickGroup }: MenuItemProps) {
  const { children } = item;
  const { open } = useShadcnSidebar();

  return (
    <div className={cn("border-t", "border-sidebar-border", "pt-4")}>
      <span
        className={cn(
          "ml-3",
          "block",
          "text-xs",
          "font-semibold",
          "uppercase",
          "text-muted-foreground",
          "transition-all",
          "duration-200",
          {
            "h-8": open,
            "h-0": !open,
            "opacity-0": !open,
            "opacity-100": open,
            "pointer-events-none": !open,
            "pointer-events-auto": open,
          },
        )}
      >
        {getDisplayName(item)}
      </span>
      {children && children.length > 0 && (
        <div className={cn("flex", "flex-col")}>
          {children.map((child: TreeMenuItem) => (
            <SidebarItem
              key={child.key || child.name}
              item={child}
              selectedKey={selectedKey}
              onClickGroup={onClickGroup}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarItemCollapsible({ item, selectedKey }: MenuItemProps) {
  const { name, children } = item;

  const chevronIcon = (
    <ChevronRight
      className={cn(
        "h-4",
        "w-4",
        "shrink-0",
        "text-muted-foreground",
        "transition-transform",
        "duration-200",
        "group-data-[state=open]:rotate-90",
      )}
    />
  );

  return (
    <Collapsible key={`collapsible-${name}`} className={cn("w-full", "group")}>
      <CollapsibleTrigger asChild>
        <SidebarButton item={item} rightIcon={chevronIcon} />
      </CollapsibleTrigger>
      <CollapsibleContent className={cn("ml-6", "flex", "flex-col", "gap-2")}>
        {children?.map((child: TreeMenuItem) => (
          <SidebarItem
            key={child.key || child.name}
            item={child}
            selectedKey={selectedKey}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function SidebarItemLink({ item, selectedKey }: MenuItemProps) {
  const isSelected = item.key === selectedKey;

  return <SidebarButton item={item} isSelected={isSelected} asLink={true} />;
}

function SidebarHeader() {
  const { title } = useRefineOptions();
  const { open, isMobile } = useShadcnSidebar();

  return (
    <ShadcnSidebarHeader
      className={cn(
        "p-0",
        "h-16",
        "border-b",
        "border-border",
        "flex-row",
        "items-center",
        "justify-between",
        "overflow-hidden",
      )}
    >
      <div
        className={cn(
          "whitespace-nowrap",
          "flex",
          "flex-row",
          "h-full",
          "items-center",
          "justify-start",
          "gap-2",
          "transition-discrete",
          "duration-200",
          {
            "pl-3": !open,
            "pl-5": open,
          },
        )}
      >
        <div>{title.icon}</div>
        <h2
          className={cn(
            "text-sm",
            "font-bold",
            "transition-opacity",
            "duration-200",
            {
              "opacity-0": !open,
              "opacity-100": open,
            },
          )}
        >
          {title.text}
        </h2>
      </div>

      <ShadcnSidebarTrigger
        className={cn("text-muted-foreground", "mr-1.5", {
          "opacity-0": !open,
          "opacity-100": open || isMobile,
          "pointer-events-auto": open || isMobile,
          "pointer-events-none": !open && !isMobile,
        })}
      />
    </ShadcnSidebarHeader>
  );
}

function getDisplayName(item: TreeMenuItem) {
  return item.meta?.label ?? item.label ?? item.name;
}

type IconProps = {
  icon: React.ReactNode;
  isSelected?: boolean;
};

function ItemIcon({ icon, isSelected }: IconProps) {
  return (
    <div
      className={cn("w-4", {
        "text-muted-foreground": !isSelected,
        "text-sidebar-primary-foreground": isSelected,
      })}
    >
      {icon ?? <ListIcon />}
    </div>
  );
}

type SidebarButtonProps = React.ComponentProps<typeof Button> & {
  item: TreeMenuItem;
  isSelected?: boolean;
  rightIcon?: React.ReactNode;
  asLink?: boolean;
  onClick?: () => void;
};

function SidebarButton({
  item,
  isSelected = false,
  rightIcon,
  asLink = false,
  className,
  onClick,
  ...props
}: SidebarButtonProps) {
  const Link = useLink();

  const buttonContent = (
    <>
      <ItemIcon icon={item.meta?.icon ?? item.icon} isSelected={isSelected} />
      <span
        className={cn("tracking-tight group-data-[collapsible=icon]:hidden", {
          "flex-1": rightIcon,
          "text-left": rightIcon,
          "line-clamp-1": !rightIcon,
          truncate: !rightIcon,
          "font-normal": !isSelected,
          "font-semibold": isSelected,
          "text-sidebar-primary-foreground": isSelected,
          "text-foreground": !isSelected,
        })}
      >
        {getDisplayName(item)}
      </span>
      {rightIcon}
    </>
  );

  return (
    <Button
      asChild={!!(asLink && item.route)}
      variant="ghost"
      size="lg"
      className={cn(
        "flex w-full items-center justify-start gap-2 py-2 px-3! text-sm",
        {
          "bg-sidebar-primary": isSelected,
          "hover:bg-sidebar-primary/90!": isSelected,
          "text-sidebar-primary-foreground": isSelected,
          "hover:text-sidebar-primary-foreground": isSelected,
        },
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {asLink && item.route ? (
        <Link to={item.route} className={cn("flex w-full items-center gap-4")}>
          {buttonContent}
        </Link>
      ) : (
        buttonContent
      )}
    </Button>
  );
}

type GroupPanelProps = {
  group: TreeMenuItem;
  selectedKey?: string;
};

function GroupPanel({ group, selectedKey }: GroupPanelProps) {
  const Link = useLink();
  const children = group.children ?? [];
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger transition on next frame so initial render is at translate-x[-100%].
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, [group.key]);

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "w-[54px]",
          "shrink-0",
          "bg-sidebar",
          "border-r",
          "border-sidebar-border",
          "py-2",
          "flex",
          "flex-col",
          "items-center",
          "gap-1",
          "transition-transform",
          "duration-200",
          "ease-out",
          {
            "-translate-x-full": !mounted,
            "translate-x-0": mounted,
          },
        )}
      >
        {children.map((child) => {
          const isSelected = child.key === selectedKey;
          const baseClassName = cn(
            "w-8 h-8 rounded-md flex items-center justify-center transition-colors",
            {
              "bg-sidebar-primary text-sidebar-primary-foreground": isSelected,
              "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground":
                !isSelected,
            },
          );
          const inner = (
            <ItemIcon
              icon={child.meta?.icon ?? child.icon}
              isSelected={isSelected}
            />
          );
          const trigger = child.route ? (
            <Link
              to={child.route}
              className={baseClassName}
              aria-label={getDisplayName(child)}
            >
              {inner}
            </Link>
          ) : (
            <div className={baseClassName} aria-label={getDisplayName(child)}>
              {inner}
            </div>
          );
          return (
            <Tooltip key={child.key || child.name}>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent side="right" sideOffset={6}>
                {getDisplayName(child)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

type RailColumnProps = {
  topLevelItems: TreeMenuItem[];
  selectedKey?: string;
  routeGroupKey?: string;
  clickGroupKey: string | null;
  onClickGroup: (key: string) => void;
  footer?: React.ReactNode;
};

function RailColumn({
  topLevelItems,
  selectedKey,
  routeGroupKey,
  clickGroupKey,
  onClickGroup,
  footer,
}: RailColumnProps) {
  const Link = useLink();

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "w-[54px]",
          "shrink-0",
          "flex-1",
          "bg-sidebar",
          "border-r",
          "border-sidebar-border",
          "py-2",
          "flex",
          "flex-col",
          "items-center",
          "gap-1",
        )}
      >
        {topLevelItems.map((item) => {
          // Skip group separator items rendered via meta.group — they have no
          // navigable presence in the rail.
          if (item.meta?.group) return null;

          const hasChildren = (item.children?.length ?? 0) > 0;
          const isRouteActive =
            item.key === selectedKey || item.key === routeGroupKey;
          const isClickFocused =
            !isRouteActive && hasChildren && item.key === clickGroupKey;

          const baseClassName = cn(
            "w-8 h-8 rounded-md flex items-center justify-center transition-colors",
            {
              "bg-sidebar-primary text-sidebar-primary-foreground":
                isRouteActive,
              "bg-sidebar-accent text-sidebar-accent-foreground":
                isClickFocused,
              "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground":
                !isRouteActive && !isClickFocused,
            },
          );

          const inner = (
            <ItemIcon
              icon={item.meta?.icon ?? item.icon}
              isSelected={isRouteActive}
            />
          );

          // Leaf with route → render as Link.
          // Group with children → render as button that triggers click-focus.
          // Avoids nesting <button> inside <a>, which is invalid HTML.
          const trigger =
            !hasChildren && item.route ? (
              <Link
                to={item.route}
                className={baseClassName}
                aria-label={getDisplayName(item)}
              >
                {inner}
              </Link>
            ) : (
              <button
                type="button"
                onClick={
                  hasChildren
                    ? () => onClickGroup(item.key ?? item.name)
                    : undefined
                }
                className={baseClassName}
                aria-label={getDisplayName(item)}
              >
                {inner}
              </button>
            );

          return (
            <Tooltip key={item.key || item.name}>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent side="right" sideOffset={6}>
                {getDisplayName(item)}
              </TooltipContent>
            </Tooltip>
          );
        })}
        {footer && (
          <>
            <div className="flex-1" />
            {footer}
          </>
        )}
      </div>
    </TooltipProvider>
  );
}

function RailUserDropdown() {
  const { mutate: logout } = useLogout();
  const { data: user } = useGetIdentity<{
    displayName: string;
    email: string;
    avatar?: string;
  }>();

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`User menu for ${user.displayName}`}
          className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-sidebar-accent/60 transition-colors"
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={user.avatar} alt={user.displayName} />
            <AvatarFallback className="text-xs">
              {user.displayName?.charAt(0)?.toUpperCase() ?? "?"}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" className="min-w-56">
        <div className="px-2 py-1.5">
          <div className="text-sm font-medium truncate">{user.displayName}</div>
          <div className="text-xs text-muted-foreground truncate">
            {user.email}
          </div>
        </div>
        <DropdownMenuItem onClick={() => logout()}>
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

Sidebar.displayName = "Sidebar";
